<template>
  <div class="grid-x">
    <div class="grid-y cell auto" id="main-content-sidebar" v-if="sidebar">
      <div id="sidebar-scroll" class="cell auto">
        <component
          :is="typeof sidebar!.handler === 'function' ? 'button' : sidebar.handler?.component || 'button'"
          v-bind="typeof sidebar!.handler === 'function' ? { type: 'button' } : sidebar!.handler?.props || {}"
          v-on="typeof sidebar!.handler === 'function' ? { click: sidebar?.handler } : {}"
          class="button expanded icon cell sidebar-button small"
          id="sidebar-title"
        >
          <span class="icon">
            <i :class="sidebar.icon" v-if="sidebar.icon"> </i>
          </span>
          <span class="h4">
            {{ $t(valueOrGetter(sidebar.title)) }}
          </span>
        </component>
        <template v-for="section of visibleSections" :key="valueOrGetter(section.title)">
          <component
            :is="typeof section.handler === 'function' ? 'button' : section.handler.component"
            v-bind="typeof section.handler === 'function' ? { type: 'button' } : section.handler.props || {}"
            v-on="typeof section.handler === 'function' ? toBindObject(section) : {}"
            :id="'sidebar-' + section.id"
            class="sidebar-section button expanded"
            :class="{
              'fc-disabled': !valueOrGetter(section.enabled),
              'active-sidebar-section': active && active === section.id,
            }"
          >
            <template v-if="section.title">
              {{ valueOrGetter(section.title) }}
            </template>
            <template v-else-if="section.titleLocale">
              {{ $t(valueOrGetter(section.titleLocale)) }}
            </template>
            <i :class="valueOrGetter(section.icon)" v-if="valueOrGetter(section.iconVisible)"> </i>
          </component>
        </template>
      </div>
      <!-- If there are Components will be rendered in the sidebar, the following code snippet will be used: -->
      <template v-if="sidebar.components">
        <template v-for="(component, index) in sidebar.components" :key="index">
          <Teleport to="body">
            <component :is="component" />
            <template v-if="sidebar?.id === 'meineLizenzen'">
              <confirmation-message :title="confirmationMessageTitle" />
            </template>
          </Teleport>
        </template>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import ConfirmationMessage from '@/components/App/ConfirmationMessage.vue'
import { componentApi } from '@/data'
import { ISidebarSection } from '@/interfaces'
import { SidebarGenerator, SidebarHandler } from '@/types'
import { valueOrGetter } from '@/utils/helpers'
import { computed, defineAsyncComponent, defineComponent, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

export default defineComponent({
  name: 'Sidebar',
  components: {
    FormRequestLicenseTicket: defineAsyncComponent(() => import('@/components/Licenses/FormRequestLicenseTicket.vue')),
    FormCreateServiceTicket: defineAsyncComponent(() => import('@/components/Service/FormCreateServiceTicket.vue')),
    BookedLicenses: defineAsyncComponent(() => import('@/components/Licenses/BookedLicenses.vue')),
    BuyDesktopLicense: defineAsyncComponent(() => import('@/components/Licenses/BuyDesktopLicense.vue')),
    ConfirmationMessage,
    componentApi,
  },
  setup() {
    const route = useRoute()
    let active = ref()
    const { t } = useI18n()
    const confirmationMessageTitle = ref(
      `${t('messages.modals.buyLicense.title')} ${t('messages.modals.buyLicense.title2')}`,
    )

    const sidebar = computed(() => {
      const sidebarGenerator: SidebarGenerator | undefined = route.meta.sidebar as SidebarGenerator | undefined
      if (!sidebarGenerator) {
        return null
      }

      return sidebarGenerator()
    })

    active = computed(() => {
      if ((route.meta.activeSidebarSection as string) !== undefined) {
        return route.meta.activeSidebarSection
      } else {
        return ''
      }
    })

    const visibleSections = computed(() =>
      sidebar.value?.sections.filter((section) => section.visible === undefined || valueOrGetter(section.visible)),
    )

    const toBindObject = (section: ISidebarSection) => {
      if (section.handler && valueOrGetter(section.enabled)) {
        return {
          click: section.handler as SidebarHandler,
        }
      }
      return {}
    }

    return {
      sidebar,
      visibleSections,
      valueOrGetter,
      toBindObject,
      active,
      confirmationMessageTitle,
    }
  },
})
</script>

<style lang="scss" scoped>
$padding-left: 3rem;

#sidebar-title {
  color: white;
  background-color: $petrol;
  padding-top: 2rem;
  padding-left: $padding-left;
  margin-bottom: 0;

  .h4,
  .icon {
    font-size: 1.5625rem;
    display: block;
    font-weight: 700;
    text-align: left;
  }

  .h4 {
    padding-left: 2rem;
  }

  .icon {
    margin: 0;
    height: 100%;
  }

  padding-bottom: 1rem;
  border-bottom: 1px $lighter-petrol-darker solid;
  border-top: none;
  cursor: pointer;
  text-transform: uppercase;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: $lighter-petrol-darker;
  }
}

.sidebar-section.button {
  font-size: 1rem;
  margin-bottom: 0;
  color: white;
  background-color: $petrol;
  box-shadow: none;
  padding: 2rem $padding-left;
  border-bottom: 1px $lighter-petrol-darker solid;
  cursor: pointer;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background-color: $secondary-color;
  }

  &.active-sidebar-section {
    background-color: $secondary-color;
  }
}
</style>
